import useDiscoveryFetch from './useDiscoveryFetch';
import { useState, useEffect } from 'react';
import api from '../../api/axiosInstance';
import { useParams } from 'react-router-dom';
import {
    Container,
    Row,
    Col,
    Card,
    Image,
    Spinner,
    Alert,
    Table,
    Form,
} from 'react-bootstrap';
import { useMediaQuery } from 'react-responsive';
import CityDetailHero from './cityDetail/CityDetailHero';
import CityDetailMobileTabs from './cityDetail/CityDetailMobileTabs';
import MobileInsightList from './MobileInsightList';
import './cityDetail/CityInsightsMobile.css';
import Pagination from 'react-bootstrap/Pagination';
import { FaCaretUp, FaCaretDown } from 'react-icons/fa';
import Sidebar from '../../Sidebar';
import Breadcrumb from '../../Breadcrumb';
import NavTabs from '../../NavTabs';
import { useMobileAiPageContext } from '../../MobileAiAssistantContext';
import { getDiscoveryAiSuggestions } from '../../utils/discoveryAiSuggestions';
import icNoInsights from '../../assets/img/ic-empty-box.svg';
import { trackEvent } from '../../services/analytics/analyticsService';
import { ANALYTICS_EVENTS } from '../../services/analytics/analyticsEvents';

const normalizeUrl = (raw) => {
    if (!raw) return null;
    try {
        const withScheme = /^[a-zA-Z][a-zA-Z0-9+\-.]*:/.test(raw) ? raw : `https://${raw}`;
        const u = new URL(withScheme);
        return ['http:', 'https:'].includes(u.protocol) ? u.toString() : null;
    } catch {
        return null;
    }
};

const applySort = (arr, { by, dir }) => {
    if (!by) return arr;
    const mul = dir === 'desc' ? -1 : 1;
    return [...arr].sort((a, b) => {
        const pick = (key, obj) => {
            switch (key) {
                case 'service':     return (obj.service || '').toLowerCase().trim();
                case 'name':        return (obj.name || '').toLowerCase().trim();
                case 'url':         return (obj.url || '').toLowerCase().trim();
                case 'rating':      return parseFloat(obj.googleRating || '0') || 0;
                default:            return '';
            }
        };
        const va = pick(by, a);
        const vb = pick(by, b);
        if (va < vb) return -1 * mul;
        if (va > vb) return  1 * mul;
        return 0;
    });
};

const DiscoveryCityViewInsights = () => {
    const isMobile = useMediaQuery({ query: '(max-width: 991.98px)' });
    const { id, cityId } = useParams();
    const actualCityId = cityId || id;
    const countryCode = cityId ? id : null;

    const [cityData, setCityData] = useState(null);
    const [insights, setInsights] = useState([]);
    const [filteredInsights, setFilteredInsights] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [searchQuery, setSearchQuery] = useState('');
    const [sort, setSort] = useState({ by: null, dir: 'asc' });
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    useDiscoveryFetch(({ signal, isCurrent, isReconnect }) => {
        const fetchCity = async () => {
            try {
                if (!isReconnect || !cityData) setLoading(true);
                setError('');
                const response = await api.get(`/api/discovery/cities/${actualCityId}/insights`, { signal });
                if (!isCurrent()) return;
                const data = response.data || {};
                setCityData(data);
                const list = Array.isArray(data.cityInsights) ? data.cityInsights : [];
                setInsights(list);
                setFilteredInsights(list);
                if (!isReconnect) setCurrentPage(1);

                if (!isReconnect) trackEvent(ANALYTICS_EVENTS.DISCOVERY_CITY_INSIGHTS_VIEWED, {
                    cityId: data?._id || actualCityId,
                    cityName: data?.name || data?.cityName || '',
                    countryCode: String(data?.country?.id || data?.country?.iso2 || '').toUpperCase(),
                    countryName: data?.country?.name || '',
                    level: 3,
                    source: 'city_insights_tab',
                });
            } catch (err) {
                if (!isCurrent()) return;
                console.error('Error fetching city insights:', err);
                setError('An error occurred while fetching city insights.');
            } finally {
                if (!isCurrent()) return;
                setLoading(false);
            }
        };

        fetchCity();
    }, [actualCityId]);

    // Search filter
    useEffect(() => {
        const q = searchQuery.trim().toLowerCase();
        const next = insights.filter((ci) => {
            if (!q) return true;
            return [ci.service, ci.name, ci.url]
                .map(v => String(v || '').toLowerCase())
                .some(s => s.includes(q));
        });
        setFilteredInsights(applySort(next, sort));
    }, [searchQuery, insights]);

    useEffect(() => { setCurrentPage(1); }, [searchQuery]);

    const toggleSort = (by) => {
        setSort(prev => {
            const dir = prev.by === by ? (prev.dir === 'asc' ? 'desc' : 'asc') : 'asc';
            const next = { by, dir };
            setFilteredInsights(applySort(filteredInsights, next));
            return next;
        });
    };

    const safeUrlHref = normalizeUrl(cityData?.url);

    const totalPages = Math.ceil(filteredInsights.length / rowsPerPage);
    const indexOfLast = currentPage * rowsPerPage;
    const indexOfFirst = indexOfLast - rowsPerPage;
    const currentInsights = filteredInsights.slice(indexOfFirst, indexOfLast);

    const cityBasePath = countryCode
        ? `/discovery/countries/${countryCode}/cities/${actualCityId}`
        : `/discovery/cities/${actualCityId}`;
    const discoveryCityNavItems = [
        { to: cityBasePath, eventKey: 'about', label: 'General Info', end: true },
        { to: `${cityBasePath}/jobs`, eventKey: 'companies', label: 'Companies & Jobs', alsoActive: [`${cityBasePath}/companies`] },
        { to: `${cityBasePath}/salary`, eventKey: 'salary', label: 'Average salaries' },
        { to: `${cityBasePath}/insights`, eventKey: 'insights', label: 'City insights' },
        { to: `${cityBasePath}/universities`, eventKey: 'universities', label: 'Universities' },
        { to: `${cityBasePath}/comments`, eventKey: 'comments', label: 'Comments' },
    ];

    // Opt this page into the shared mobile AI assistant (header trigger + bottom sheet).
    useMobileAiPageContext({
        pageType: 'discovery-city',
        discoveryType: 'cities',
        cityId: actualCityId,
        cityName: cityData?.name || cityData?.cityName || '',
        countryName: cityData?.country?.name || '',
        activeTab: 'City Insights',
        suggestions: getDiscoveryAiSuggestions({
            discoveryType: 'cities',
            selectedCity: cityData?.name || cityData?.cityName || '',
            selectedCountry: cityData?.country?.name || '',
        }),
    });

    if (loading) {
        return (
            <>
                <Sidebar />
                <main className="main-content flex-grow-1">
                    <Container>
                        <Row>
                            <Col className="py-5 text-center">
                                <Spinner animation="border" />
                            </Col>
                        </Row>
                    </Container>
                </main>
            </>
        );
    }

    if (error) {
        return (
            <>
                <Sidebar />
                <main className="main-content flex-grow-1">
                    <Container>
                        <Row>
                            <Col>
                                <Alert variant="danger" className="mt-4">{error}</Alert>
                            </Col>
                        </Row>
                    </Container>
                </main>
            </>
        );
    }

    if (!cityData) {
        return (
            <>
                <Sidebar />
                <main className="main-content flex-grow-1">
                    <Container>
                        <Row>
                            <Col>
                                <Alert variant="warning" className="mt-4">City not found.</Alert>
                            </Col>
                        </Row>
                    </Container>
                </main>
            </>
        );
    }

    return (
        <>
            <Sidebar />
            <main className="main-content flex-grow-1">
                <Container>
                    <Row>
                        <Col>
                            <Breadcrumb cityName={cityData?.name} subPage="City Insights" fromCountry={countryCode} countryName={cityData?.country?.name} />
                        </Col>
                    </Row>

                    {isMobile ? (
                        <Row className="g-0 country-detail-mobile city-detail-mobile">
                            <Col className="p-0">
                                <CityDetailHero cityName={cityData.name}
                                    url={safeUrlHref ? { href: safeUrlHref, label: cityData.url } : null}
                                    countryName={cityData.country?.name} />
                                <CityDetailMobileTabs items={discoveryCityNavItems} />
                                <Card className="country-detail-content-card mb-4">
                                    <Card.Body>
                                        <Card className="company-detail-section mb-4 city-insights-section">
                                            <Card.Body>
                                                <h3 className="company-detail-section-title">
                                                    City insights
                                                </h3>
                                                <hr />
                                                <MobileInsightList key={cityBasePath} insights={insights}
                                                    cityId={actualCityId} countryId={countryCode}
                                                    searchQuery={searchQuery} onSearchChange={setSearchQuery}
                                                    normalizeUrl={normalizeUrl} />
                                            </Card.Body>
                                        </Card>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    ) : (
                    <Row>
                        <Col>
                            <Card className="mb-4">
                                <Card.Body className="pt-0">
                                    <Row>
                                        <Col xs={12}>
                                            <Row>
                                                <Col>
                                                    <Card className="card-premium">
                                                        <Card.Body>
                                                            <Row className="align-items-center">
                                                                <Col>
                                                                    <h3 className="text-start">City</h3>
                                                                    <hr />
                                                                </Col>
                                                            </Row>
                                                            <Row className="mb-4">
                                                                <Col md={8} className="d-flex">
                                                                    <div>
                                                                        <h1>{cityData.name || '—'}</h1>
                                                                        <p>
                                                                            {safeUrlHref ? (
                                                                                <a href={safeUrlHref} target="_blank" rel="noopener noreferrer">
                                                                                    {cityData.url}
                                                                                </a>
                                                                            ) : '—'}
                                                                            {cityData.country?.name && (
                                                                                <>
                                                                                    <span className="dot"></span>
                                                                                    Country: <strong>{cityData.country.name}</strong>
                                                                                </>
                                                                            )}
                                                                        </p>
                                                                    </div>
                                                                </Col>
                                                            </Row>
                                                        </Card.Body>
                                                    </Card>
                                                </Col>
                                            </Row>

                                            <Row className="mb-5">
                                                <Col>
                                                    <NavTabs items={discoveryCityNavItems} />
                                                </Col>
                                            </Row>

                                            {/* Insights card */}
                                            <Card className="mb-4">
                                                <Card.Body>
                                                    <Row className="align-items-center">
                                                        <Col>
                                                            <h3 className="mb-0">City Insights</h3>
                                                        </Col>
                                                    </Row>
                                                    <hr className="pt-4" />

                                                    {/* Search */}
                                                    <Row className="mb-3">
                                                        <Col xs={12} md={4}>
                                                            <Form.Control
                                                                type="text"
                                                                placeholder="Search insights..."
                                                                value={searchQuery}
                                                                onChange={(e) => setSearchQuery(e.target.value)}
                                                            />
                                                        </Col>
                                                    </Row>

                                                    {filteredInsights.length === 0 ? (
                                                        <Row>
                                                            <Col md={12}>
                                                                <Row className="mb-3">
                                                                    <Col md={6} className="no-result offset-md-3 d-flex flex-column justify-content-center align-items-center text-center">
                                                                        <Image src={icNoInsights} alt="No City Insights" className="mb-4" />
                                                                        <h2 className="mb-3">No city insights yet.</h2>
                                                                        <p className="mb-4">City insights will be displayed here.</p>
                                                                    </Col>
                                                                </Row>
                                                            </Col>
                                                        </Row>
                                                    ) : (
                                                        <>
                                                            <Row>
                                                                <Col>
                                                                    <div className="table-responsive list">
                                                                        <Table>
                                                                            <thead>
                                                                                <tr>
                                                                                    <th onClick={() => toggleSort('service')} style={{ cursor: 'pointer' }}>
                                                                                        <div>
                                                                                            Service
                                                                                            <span>
                                                                                                <FaCaretUp className={sort.by === 'service' && sort.dir === 'asc' ? 'text-primary' : ''} />
                                                                                                <FaCaretDown className={sort.by === 'service' && sort.dir === 'desc' ? 'text-primary' : ''} />
                                                                                            </span>
                                                                                        </div>
                                                                                    </th>
                                                                                    <th onClick={() => toggleSort('name')} style={{ cursor: 'pointer' }}>
                                                                                        <div>
                                                                                            Provider name
                                                                                            <span>
                                                                                                <FaCaretUp className={sort.by === 'name' && sort.dir === 'asc' ? 'text-primary' : ''} />
                                                                                                <FaCaretDown className={sort.by === 'name' && sort.dir === 'desc' ? 'text-primary' : ''} />
                                                                                            </span>
                                                                                        </div>
                                                                                    </th>
                                                                                    <th onClick={() => toggleSort('url')} style={{ cursor: 'pointer' }}>
                                                                                        <div>
                                                                                            URL
                                                                                            <span>
                                                                                                <FaCaretUp className={sort.by === 'url' && sort.dir === 'asc' ? 'text-primary' : ''} />
                                                                                                <FaCaretDown className={sort.by === 'url' && sort.dir === 'desc' ? 'text-primary' : ''} />
                                                                                            </span>
                                                                                        </div>
                                                                                    </th>
                                                                                    <th onClick={() => toggleSort('rating')} style={{ cursor: 'pointer' }}>
                                                                                        <div>
                                                                                            Google rating
                                                                                            <span>
                                                                                                <FaCaretUp className={sort.by === 'rating' && sort.dir === 'asc' ? 'text-primary' : ''} />
                                                                                                <FaCaretDown className={sort.by === 'rating' && sort.dir === 'desc' ? 'text-primary' : ''} />
                                                                                            </span>
                                                                                        </div>
                                                                                    </th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {currentInsights.map((ins) => {
                                                                                    const href = normalizeUrl(ins.url);
                                                                                    return (
                                                                                        <tr key={ins._id || `${ins.name}.${ins.service}`}>
                                                                                            <td>{ins.service || '—'}</td>
                                                                                            <td>{ins.name || '—'}</td>
                                                                                            <td>
                                                                                                {href
                                                                                                    ? <a href={href} target="_blank" rel="noopener noreferrer">{ins.url}</a>
                                                                                                    : '—'}
                                                                                            </td>
                                                                                            <td>{(ins.googleRating ?? '') === '' ? '—' : ins.googleRating}</td>
                                                                                        </tr>
                                                                                    );
                                                                                })}
                                                                            </tbody>
                                                                        </Table>
                                                                    </div>
                                                                </Col>
                                                            </Row>

                                                            <Row className="pagination-wrapper">
                                                                <Col className="d-flex align-items-center">
                                                                    <span>Total&nbsp;{filteredInsights.length}&nbsp;Insights</span>
                                                                    <Pagination>
                                                                        <Pagination.Prev
                                                                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                                                            disabled={currentPage === 1}
                                                                        />
                                                                        {[...Array(totalPages).keys()].map(number => (
                                                                            <Pagination.Item
                                                                                key={number + 1}
                                                                                active={number + 1 === currentPage}
                                                                                onClick={() => setCurrentPage(number + 1)}
                                                                            >
                                                                                {number + 1}
                                                                            </Pagination.Item>
                                                                        ))}
                                                                        <Pagination.Next
                                                                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                                                            disabled={currentPage === totalPages}
                                                                        />
                                                                    </Pagination>
                                                                </Col>
                                                                <Col className="pagination-controls">
                                                                    <div className="pagination-controls-per-page">
                                                                        <Form.Select
                                                                            value={rowsPerPage}
                                                                            onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                                                                            style={{ width: 'auto', marginRight: '10px' }}
                                                                        >
                                                                            <option value="10">10 / page</option>
                                                                            <option value="20">20 / page</option>
                                                                            <option value="40">40 / page</option>
                                                                        </Form.Select>
                                                                    </div>
                                                                    <label className="pagination-controls-goto-label" htmlFor="goto-page-insights">
                                                                        Go to
                                                                    </label>
                                                                    <Form.Control
                                                                        id="goto-page-insights"
                                                                        type="number"
                                                                        className="pagination-controls-goto-input"
                                                                        value={currentPage}
                                                                        onChange={(e) => {
                                                                            const newPage = Number(e.target.value);
                                                                            if (newPage >= 1 && newPage <= totalPages) {
                                                                                setCurrentPage(newPage);
                                                                            }
                                                                        }}
                                                                        onBlur={() => {
                                                                            if (currentPage < 1) setCurrentPage(1);
                                                                            if (currentPage > totalPages) setCurrentPage(totalPages);
                                                                        }}
                                                                        style={{ textAlign: 'center' }}
                                                                    />
                                                                </Col>
                                                            </Row>
                                                        </>
                                                    )}
                                                </Card.Body>
                                            </Card>

                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                    )}
                </Container>
            </main>
        </>
    );
};

export default DiscoveryCityViewInsights;
